import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { IconDirective } from '@coreui/icons-angular';
import { BugReport } from '../../models/bug-reports/bug-report.model';
import { BugCategoryType } from '../../enums/bug-reports/bug-category-type.enum';
import { ApiBugReportsService } from '../../services/bug-reports/api/api-bug-reports.service';
import { AlertsService } from '../../services/alerts.service';
import { DateService } from '../../services/date.service';
import { BugReportDetailModalComponent } from './modals/bug-report-detail-modal/bug-report-detail-modal.component';
import { NewsUserAddModalComponent } from '../users/user-details/tabs/user-messages-tab/modals/news-user-add-modal/news-user-add-modal.component';
import { UserMatchDetailModalComponent } from '../users/user-details/tabs/user-matchs-tab/modals/user-match-detail-modal/user-match-detail-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';

@Component({
  templateUrl: 'bug-reports.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule, TabsModule, IconDirective, BugReportDetailModalComponent, NewsUserAddModalComponent, UserMatchDetailModalComponent, ConfigurationsBlockComponent]
})
export class BugReportsComponent implements OnInit {

  @ViewChild('detailModal') detailModal!: BugReportDetailModalComponent;
  @ViewChild('newsUserAddModal') newsUserAddModal!: NewsUserAddModalComponent;
  @ViewChild('matchDetailModal') matchDetailModal!: UserMatchDetailModalComponent;

  readonly bugReportsConfigurations: ConfigurationDef[] = [
    { name: 'bug_reports_clean_timeout', label: 'Clean Timeout', type: 'number' },
  ];

  BugCategoryType = BugCategoryType;

  reports: BugReport[] = [];
  isLoading: boolean = false;
  totalResults: number = 0;
  private static pageNumber: number = 1;
  readonly resultsPerPage: number = 25;

  get pageNumber(): number { return BugReportsComponent.pageNumber; }
  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  constructor(
    public dateService: DateService,
    private apiBugReportsService: ApiBugReportsService,
    private alertsService: AlertsService,
  ) {}

  ngOnInit(): void {
    this.refreshReports();
  }

  getPageNumbers(current: number, total: number): (number | null)[] {
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { BugReportsComponent.pageNumber = page; this.refreshReports(); }
  onClickPaginationPrevious(): void { BugReportsComponent.pageNumber--; this.refreshReports(); }
  onClickPaginationNext(): void { BugReportsComponent.pageNumber++; this.refreshReports(); }

  getDisplayName(report: BugReport): string {
    return report.userDisplayname ? report.userDisplayname : 'Guest' + report.userId;
  }

  getLocaleFlagIconName(locale: string): string {
    if (!locale) return '';
    const parts = locale.split('_');
    const countryCode = (parts.length > 1 ? parts[1] : parts[0]).toLowerCase();
    return 'cif' + countryCode.charAt(0).toUpperCase() + countryCode.slice(1);
  }

  getBugTypeName(bugType: number): string {
    switch (bugType) {
      case BugCategoryType.CreditLost: return 'Credit Lost';
      case BugCategoryType.BuyIssue: return 'Buy Issue';
      case BugCategoryType.InGameIssue: return 'In-Game Issue';
      case BugCategoryType.GameIssue: return 'Game Issue';
      case BugCategoryType.Other: return 'Other';
      default: return 'None';
    }
  }

  onClickDetails(report: BugReport): void {
    this.detailModal.show(report);
  }

  onClickLastMatch(report: BugReport): void {
    if (!report.lastMatchId) return;
    this.matchDetailModal.show(report.userId, report.lastMatchId);
  }

  onClickSendMessage(report: BugReport): void {
    this.newsUserAddModal.show(report.userId, report.userLocale, () => {
      this.apiBugReportsService.changeReportState(report.bugReportId, true).then(
        () => {
          this.alertsService.displayAlertSuccess('Message sent and report closed !');
          this.refreshReports();
        }, error => {
          this.alertsService.displayAlertError('Error closing report : ' + error);
        }
      );
    });
  }

  onClickToggleState(report: BugReport): void {
    const newState = !report.isClosed;
    this.apiBugReportsService.changeReportState(report.bugReportId, newState).then(
      () => {
        this.alertsService.displayAlertSuccess(newState ? 'Report closed !' : 'Report reopened !');
        this.refreshReports();
      }, error => {
        this.alertsService.displayAlertError('Error changing report state : ' + error);
      }
    );
  }

  private refreshReports(): void {
    this.isLoading = true;
    this.apiBugReportsService.getReports(this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.reports = response.results;
        this.totalResults = response.resultsTotal;
        if (this.pageNumber > this.totalPages && this.totalPages > 0) {
          BugReportsComponent.pageNumber = 1;
          this.refreshReports();
        }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading bug reports : ' + error);
      }
    );
  }
}
